import { Injectable, signal } from '@angular/core';

export interface CacheItem<T> {
  data: T;
  expiry: number;
  key: string;
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private storage: Storage = sessionStorage;
  private prefix = 'fs_cache_';

  constructor() {
    this.loadFromStorage();
  }

  private getStorageKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  set<T>(key: string, data: T, ttlMinutes: number = 5): void {
    const expiry = Date.now() + (ttlMinutes * 60 * 1000);
    const item: CacheItem<T> = { data, expiry, key };
    this.cache.set(key, item);
    this.persistToStorage(key, item);
  }

  get<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() > cached.expiry) {
      this.invalidate(key);
      return null;
    }

    return cached.data as T;
  }

  has(key: string): boolean {
    const cached = this.cache.get(key);
    if (!cached) return false;
    if (Date.now() > cached.expiry) {
      this.invalidate(key);
      return false;
    }
    return true;
  }

  invalidate(key?: string): void {
    if (key) {
      this.cache.delete(key);
      this.storage.removeItem(this.getStorageKey(key));
    } else {
      this.clear();
    }
  }

  clear(): void {
    this.cache.clear();
    Object.keys(this.storage)
      .filter(k => k.startsWith(this.prefix))
      .forEach(k => this.storage.removeItem(k));
  }

  private persistToStorage(key: string, item: CacheItem<any>): void {
    try {
      const serializable = {
        data: item.data,
        expiry: item.expiry,
        key: item.key
      };
      this.storage.setItem(this.getStorageKey(key), JSON.stringify(serializable));
    } catch (e) {
      console.warn('[Cache] Storage quota exceeded, clearing old items');
      this.clearOldest();
    }
  }

  private loadFromStorage(): void {
    try {
      Object.keys(this.storage)
        .filter(k => k.startsWith(this.prefix))
        .forEach(k => {
          const raw = this.storage.getItem(k);
          if (!raw) return;
          const item = JSON.parse(raw) as CacheItem<any>;
          if (item.expiry > Date.now()) {
            this.cache.set(item.key, item);
          } else {
            this.storage.removeItem(k);
          }
        });
    } catch (e) {
      console.warn('[Cache] Error loading from storage:', e);
    }
  }

  private clearOldest(): void {
    const entries = Array.from(this.cache.entries());
    if (entries.length === 0) return;
    const oldest = entries.sort((a, b) => a[1].expiry - b[1].expiry)[0];
    this.invalidate(oldest[0]);
  }

  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}
